import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  ICarouselItem,
  IVerticalCarousel,
} from "Interfaces/interface.informationalPages";
import { ourCulture } from "@/data/aboutUs";

const VerticalCarousel: React.FC<IVerticalCarousel> = ({
  items,
  speed,
  direction,
  height,
  itemHeight,
}) => {
  const carouselInnerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isVisible = useInView(wrapperRef, { once: true, margin: "100px" });
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const duplicatedItems: ICarouselItem[] = [...items, ...items, ...items];
  const cycleHeight = items.length * itemHeight;

  useEffect(() => {
    let animationFrameId: number;

    const animateScroll = () => {
      if (!isPaused) {
        setScrollPosition((prevScrollPosition) => {
          let newScrollPosition: number;

          if (direction === "down") {
            newScrollPosition = prevScrollPosition + speed;
            if (newScrollPosition >= cycleHeight) {
              newScrollPosition -= cycleHeight;
            }
          } else {
            newScrollPosition = prevScrollPosition - speed;
            if (newScrollPosition < 0) {
              newScrollPosition += cycleHeight;
            }
          }
          return newScrollPosition;
        });
      }
      animationFrameId = requestAnimationFrame(animateScroll);
    };

    animationFrameId = requestAnimationFrame(animateScroll);
    return () => cancelAnimationFrame(animationFrameId);
  }, [speed, direction, items.length, itemHeight, cycleHeight, isPaused]);

  const innerContentHeight = duplicatedItems.length * itemHeight;

  return (
    <div
      ref={wrapperRef}
      className="relative overflow-hidden w-[300px] rounded-lg"
      style={{ height: `${height}px` }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Top Fade */}
      <div className="absolute top-0 left-0 right-0 h-[40px] z-10 pointer-events-none bg-gradient-to-b from-white to-transparent" />

      {/* Carousel Content */}
      <div
        ref={carouselInnerRef}
        className="flex flex-col will-change-transform max-w-xs min-w-28"
        style={{
          transform: `translateY(${-scrollPosition}px)`,
          height: `${innerContentHeight}px`,
        }}
      >
        {duplicatedItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="w-full box-border flex flex-col items-center justify-center p-2 text-neutral-800 text-sm text-center"
            style={{ height: `${itemHeight}px` }}
          >
            <div className="w-full h-[168px] flex items-center justify-center text-gray-500 text-2xl font-bold mb-1 overflow-hidden rounded-lg">
              {isVisible ? (
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 animate-pulse">
                  {item.alt}
                </div>
              )}
            </div>
            <span className="text-sm text-black dark:text-neutral-100 font-inter">
              {item.alt}
            </span>
          </div>
        ))}
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-[40px] z-10 pointer-events-none bg-gradient-to-t from-white to-transparent" />
    </div>
  );
};

const App: React.FC = () => {
  const shuffleArray = (array: ICarouselItem[]): ICarouselItem[] => {
    let newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const carouselItemHeight: number = 168 + 16 + 4 + 4 + 14;
  const visibleItemsCount: number = 1.9;
  const fadeOverlayHeight: number = 50;
  const carouselHeight: number =
    carouselItemHeight * visibleItemsCount + fadeOverlayHeight * 2;

  const wrapperVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.9,
        ease: "linear" as const,
        staggerChildren: 0.3,
      },
    },
  };

  const carouselVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-start py-8"
      variants={wrapperVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="flex justify-center items-start gap-8 flex-wrap">
        <motion.div variants={carouselVariants}>
          {/* First Carousel */}
          <VerticalCarousel
            items={shuffleArray(ourCulture)}
            speed={0.8}
            direction="down"
            height={carouselHeight}
            itemHeight={carouselItemHeight}
          />
        </motion.div>

        <motion.div
          className="hidden md:inline-block"
          variants={carouselVariants}
        >
          {/* Second Carousel */}
          <VerticalCarousel
            items={shuffleArray(ourCulture)}
            speed={1}
            direction="up"
            height={carouselHeight}
            itemHeight={carouselItemHeight}
          />
        </motion.div>

        <motion.div
          className="hidden xl:inline-block"
          variants={carouselVariants}
        >
          {/* Third Carousel */}
          <VerticalCarousel
            items={shuffleArray(ourCulture)}
            speed={0.6}
            direction="down"
            height={carouselHeight}
            itemHeight={carouselItemHeight}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default App;
